import {
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import {
  Avatar,
  Badge,
  Box,
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineColorScheme
} from '@mantine/core';
import { ProfileCompletionSummary } from '../../../features/ProfileCompletion/components/ProfileCompletionSummary';
import { Person } from '../../../models/person.model';
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';

interface CVPreviewHeaderProps {
  person: Person;
}

export function CVPreviewHeader({ person }: CVPreviewHeaderProps) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not specified';

    const date = new Date(dateString);

    if (date.getFullYear() <= 1) {
      return 'Not specified';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const initials = getInitials(person.firstName, person.lastName);
  const fullName = `${person.firstName} ${person.lastName}`;

  return (
    <Box
      p="md"
      style={{
        flexShrink: 0,
        background: isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)',
        borderBottom: '1px solid var(--mantine-color-gray-3)',
        position: 'relative'
      }}
    >
      {/* Row: photo left · badges + position right */}
      <Group gap="md" align="flex-start">
        <Avatar
          size={120}
          radius="xl"
          color="brand"
          variant="light"
          src={person.imagePath}
          style={{ border: '3px solid var(--mantine-color-brand-5)', flexShrink: 0 }}
        >
          {!person.imagePath && initials}
        </Avatar>

        <Box style={{ flex: 1, minWidth: 0 }}>
          {/* Stacked date cards — shrink to content width */}
          <Stack gap={6} mb={isPersonCurrentlyUnassigned(person.availabilityDate) || person.inactive ? 'xs' : 0} style={{ alignItems: 'flex-start' }}>
            <Card shadow="sm" p="6px 10px" radius="md" withBorder style={{ display: 'inline-block' }}>
              <Text size="xs" c="dimmed" mb={1}>CV Updated</Text>
              <Text size="sm" fw={600} c="brand">{formatDate(person.cvUpdateDate?.toString())}</Text>
            </Card>
            <Card shadow="sm" p="6px 10px" radius="md" withBorder style={{ display: 'inline-block' }}>
              <Text size="xs" c="dimmed" mb={1}>Availability</Text>
              <Text
                size="sm"
                fw={600}
                c={person.availabilityDate && new Date(person.availabilityDate).getFullYear() > 1 ? 'orange.9' : 'dimmed'}
              >
                {formatDate(person.availabilityDate)}
              </Text>
            </Card>
          </Stack>

          {/* Status badges */}
          <Group gap="xs" wrap="wrap">
            {isPersonCurrentlyUnassigned(person.availabilityDate) && (
              <Badge color="orange" size="sm" variant="filled">Unassigned</Badge>
            )}
            {person.inactive && (
              <Badge color="red" size="sm" variant="filled">Inactive</Badge>
            )}
          </Group>
        </Box>
      </Group>

      {/* Name — always fully visible */}
      <Title order={4} mt="sm" style={{ wordBreak: 'break-word' }}>{fullName}</Title>
      <Text c="dimmed" size="sm" mb={2}>{person.positions?.[0] || 'No position specified'}</Text>

      {/* Email */}
      <Box mt={4} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ThemeIcon size="sm" radius="xl" color="gray" variant="light">
          <EnvelopeIcon style={{ width: 14, height: 14 }} />
        </ThemeIcon>
        <Text size="sm" style={{ wordBreak: 'break-all' }}>{person.email}</Text>
      </Box>

      {/* Profile completion — full width, last item before border */}
      <Box mt="sm">
        <ProfileCompletionSummary personId={person.id} />
      </Box>

    </Box>
  );
}

